/**
 * AdminDensityOverlay — Phase F.3 admin-only density layer.
 *
 * Visible ONLY to logged-in admins (gated by `useAuth().isAdmin`).
 * Renders a toggle button + collapsible legend in the Map hero
 * section. When the toggle is enabled:
 *
 *   1. Fetches `/api/admin/coverage/density?window_days=90` with
 *      the admin bearer token.
 *   2. Calls `onDensityChange(density)` so the parent Map.jsx can
 *      thread the data into the Mapbox layer stack (per-ZIP bin
 *      colouring on top of the base served/unserved layer).
 *   3. Renders a per-bin legend + the surviving-ZIP list with
 *      booking counts.
 *
 * Privacy: the endpoint already enforces suppression server-side
 * (ZIPs with booking_count < 5 are omitted entirely). The FE
 * surfaces only the data it receives — it NEVER tries to display
 * suppressed ZIPs and never falls back to per-partner numbers if
 * the response is empty.
 */
import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { API_BASE } from "@/lib/api";

const BIN_COLORS = {
  low:  "#FCD8B0",   // soft peach
  mid:  "#F89623",   // PooPlucker orange
  high: "#C4571B",   // burnt orange
};

const BIN_LABELS = {
  low:  "Low (5–14)",
  mid:  "Mid (15–49)",
  high: "High (50+)",
};

export default function AdminDensityOverlay({ onDensityChange, onZipFocus }) {
  const { isAdmin } = useAuth();
  const [enabled, setEnabled] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!enabled) {
      onDensityChange?.(null);
      setData(null);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const token = localStorage.getItem("pp_token");
        const r = await fetch(
          `${API_BASE}/admin/coverage/density?window_days=90`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (!r.ok) {
          // 401/403 means an admin's session lapsed — show a friendly
          // line instead of leaking the response body.
          if (r.status === 401 || r.status === 403) {
            throw new Error("Session expired — please sign in again.");
          }
          throw new Error("Couldn't load density data.");
        }
        const payload = await r.json();
        if (cancelled) return;
        setData(payload);
        onDensityChange?.(payload);
      } catch (e) {
        if (!cancelled) {
          setError(e.message || "Something went wrong.");
          setData(null);
          onDensityChange?.(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [enabled, onDensityChange]);

  if (!isAdmin) return null;

  return (
    <div
      className="rounded-2xl p-4 lg:p-5 mt-4"
      style={{
        background: "var(--pp-v3-surface)",
        border: "1px solid var(--pp-line)",
      }}
      data-testid="map-admin-density-panel"
    >
      <div className="flex items-center justify-between gap-3">
        <div>
          <div
            className="pp-kick"
            style={{ fontSize: 11, color: "var(--pp-text-soft)" }}
          >
            Admin only · Density overlay
          </div>
          <div
            className="mt-1"
            style={{ fontSize: 14, color: "var(--pp-text)" }}
          >
            Per-ZIP booking heat over the last 90 days.{" "}
            <strong>ZIPs with fewer than 5 bookings are suppressed</strong>{" "}
            for privacy.
          </div>
        </div>
        <button
          type="button"
          onClick={() => setEnabled((v) => !v)}
          data-testid="map-admin-density-toggle"
          className="rounded-md px-3 py-2 text-sm font-semibold"
          style={{
            background: enabled ? "var(--pp-orange)" : "var(--pp-bg)",
            color: enabled ? "white" : "var(--pp-text)",
            border: "1px solid var(--pp-line)",
            minWidth: 96,
          }}
        >
          {enabled ? "On" : "Off"}
        </button>
      </div>
      {enabled && loading && (
        <div
          className="mt-3 text-sm"
          style={{ color: "var(--pp-text-soft)" }}
          data-testid="map-admin-density-loading"
        >
          Loading density…
        </div>
      )}
      {enabled && error && (
        <div
          className="mt-3 text-sm"
          style={{ color: "#B91C1C" }}
          data-testid="map-admin-density-error"
        >
          {error}
        </div>
      )}
      {enabled && data && !loading && !error && (
        <div className="mt-4" data-testid="map-admin-density-legend">
          {/* Legend row — colored swatches per bin */}
          <div className="flex flex-wrap items-center gap-3">
            {["low", "mid", "high"].map((bin) => (
              <span
                key={bin}
                className="inline-flex items-center gap-2 text-xs"
                style={{ color: "var(--pp-text)" }}
                data-testid={`map-admin-density-legend-${bin}`}
              >
                <span
                  style={{
                    width: 14, height: 14, borderRadius: 3,
                    background: BIN_COLORS[bin],
                    border: "1px solid rgba(0,0,0,0.08)",
                    display: "inline-block",
                  }}
                />
                {BIN_LABELS[bin]}
              </span>
            ))}
          </div>
          {/* Aggregates */}
          <div
            className="mt-3 grid grid-cols-3 gap-3 text-sm"
            style={{ color: "var(--pp-text)" }}
            data-testid="map-admin-density-stats"
          >
            <div>
              <div style={{ fontSize: 11, color: "var(--pp-text-soft)" }}>
                Total counted
              </div>
              <div style={{ fontWeight: 700, fontSize: 18 }}>
                {data.total_counted}
              </div>
            </div>
            <div>
              <div style={{ fontSize: 11, color: "var(--pp-text-soft)" }}>
                ZIPs visible
              </div>
              <div style={{ fontWeight: 700, fontSize: 18 }}>
                {(data.zips || []).length}
              </div>
            </div>
            <div>
              <div style={{ fontSize: 11, color: "var(--pp-text-soft)" }}>
                ZIPs suppressed (&lt;5)
              </div>
              <div
                style={{ fontWeight: 700, fontSize: 18 }}
                data-testid="map-admin-density-suppressed-count"
              >
                {data.suppressed_zip_count}
              </div>
            </div>
          </div>
          {/* Surviving ZIPs list */}
          {(data.zips || []).length > 0 && (
            <ul
              className="mt-3 flex flex-col gap-1"
              data-testid="map-admin-density-zip-list"
            >
              {data.zips.map((row) => (
                <li
                  key={row.zip}
                  className="flex items-center justify-between rounded-md px-3 py-2"
                  style={{
                    background: "var(--pp-bg)",
                    border: "1px solid var(--pp-line)",
                  }}
                  data-testid={`map-admin-density-row-${row.zip}`}
                >
                  <span className="flex items-center gap-2">
                    <span
                      style={{
                        width: 10, height: 10, borderRadius: 2,
                        background: BIN_COLORS[row.bin] || "#ccc",
                        display: "inline-block",
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => onZipFocus?.(row.zip)}
                      style={{
                        fontFamily: "ui-monospace, monospace",
                        color: "var(--pp-orange)",
                        textDecoration: "underline",
                        fontWeight: 600,
                      }}
                    >
                      {row.zip}
                    </button>
                  </span>
                  <span style={{ color: "var(--pp-text-soft)" }}>
                    <strong style={{ color: "var(--pp-text)" }}>
                      {row.booking_count}
                    </strong>{" "}
                    bookings
                  </span>
                </li>
              ))}
            </ul>
          )}
          {(data.zips || []).length === 0 && (
            <div
              className="mt-3 text-sm"
              style={{ color: "var(--pp-text-soft)" }}
              data-testid="map-admin-density-empty"
            >
              No ZIP has more than 5 bookings in the last 90 days yet.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export { BIN_COLORS };
